// Juntar fornecedores duplicados (01/10/2026). Espelha o ModalMesclarClientes
// (ComercialClientes.tsx), com a diferença que importa aqui: cada cartão mostra
// CNPJ, nº de lançamentos e total pago — é o que a pessoa precisa para decidir
// quem FICA. O servidor move os lançamentos e apaga os outros; não há desfazer.
import { useState } from 'react';
import axios from 'axios';
import { Modal, Alert, Card, Flex, Space, Tag, Typography, App as AntApp } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { formatarCnpjCpf } from '../../utils/fornecedores';
import {
  FornecedorLinha, escolherMantido, cnpjsDistintos, semCnpj, fmtDinheiro, mensagemDeErro,
} from './logica';
import { fmtDataBr } from '../../utils/dataFmt';

const { Text } = Typography;

export default function ModalMesclarFornecedores({ fornecedores, onFechar, onSalvou }: {
  fornecedores: FornecedorLinha[];
  onFechar: () => void;
  onSalvou: () => void;
}) {
  const { message } = AntApp.useApp();
  // Pré-seleciona o que tem CNPJ; empate → o de mais lançamentos.
  const [manterId, setManterId] = useState<string | null>(() => escolherMantido(fornecedores));
  const [salvando, setSalvando] = useState(false);
  // O 409 do servidor traz uma frase longa (quantos lançamentos colidiram);
  // um toast some antes de dar para ler. Fica dentro do modal.
  const [erro, setErro] = useState<string | null>(null);

  const diferentes = cnpjsDistintos(fornecedores);
  // Manter um cadastro INATIVO leva os lançamentos para quem os dropdowns de
  // despesa não oferecem (eles pedem ativo=true): o fornecedor sumiria da vida
  // prática sem ninguém perceber. Avisa, não bloqueia — pode ser proposital.
  const mantidoInativo = fornecedores.find(f => f.id === manterId && !f.ativo);

  async function juntar() {
    if (!manterId) { message.warning('Escolha qual fornecedor manter.'); return; }
    const excluirIds = fornecedores.filter(f => f.id !== manterId).map(f => f.id);
    if (!excluirIds.length) { message.warning('Selecione pelo menos 2 fornecedores.'); return; }
    setSalvando(true);
    setErro(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/fornecedores/mesclar`,
        { manter_id: manterId, excluir_ids: excluirIds },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const aproveitados: string[] = Array.isArray(d.campos_aproveitados) ? d.campos_aproveitados : [];
      message.success(
        `Fornecedores juntados: ${Number(d.lancamentos_movidos) || 0} lançamento(s) movido(s)` +
        (aproveitados.length ? `; dados aproveitados: ${aproveitados.join(', ')}` : '') + '.',
        6,
      );
      onSalvou();
    } catch (e: any) {
      setErro(mensagemDeErro(e, 'Erro ao juntar'));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onFechar} onOk={juntar} confirmLoading={salvando} destroyOnClose centered
      title={`Juntar ${fornecedores.length} fornecedores`} okText="Juntar" okType="danger" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 680 }}
      styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' } }}>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message="Operação irreversível"
        description="Os fornecedores não escolhidos serão APAGADOS. Todos os lançamentos deles passam para o fornecedor que ficar, e o nome dele passa a valer em todos." />
      {diferentes > 1 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`Estes fornecedores têm ${diferentes} CNPJs diferentes`}
          description="Pode ser a mesma empresa com filiais, e cada filial tem o seu CNPJ. Junte só se for mesmo o mesmo cadastro repetido." />
      )}
      {mantidoInativo && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="O fornecedor escolhido para ficar está inativo"
          description="Os lançamentos vão para ele, mas um cadastro inativo não aparece na lista de escolha ao criar despesa. Reative-o depois, ou escolha outro para ficar." />
      )}
      {erro && <Alert type="error" showIcon closable onClose={() => setErro(null)} style={{ marginBottom: 12 }}
        message="Não foi possível juntar" description={erro} />}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Qual fornecedor MANTER?</Text>
      <Space direction="vertical" style={{ width: '100%' }}>
        {fornecedores.map(f => {
          const fica = manterId === f.id;
          return (
            <Card key={f.id} size="small" hoverable
              style={{
                cursor: 'pointer',
                borderColor: fica ? '#0930D0' : undefined,
                borderWidth: fica ? 2 : 1,
                background: fica ? '#F0F4FF' : undefined,
              }}
              onClick={() => setManterId(f.id)}>
              <Flex justify="space-between" align="center" gap={8}>
                <div style={{ minWidth: 0 }}>
                  <Text strong>{f.nome_fantasia || f.razao_social || '(sem nome)'}</Text>
                  {f.razao_social && f.razao_social !== f.nome_fantasia && (
                    <Text type="secondary"> · {f.razao_social}</Text>
                  )}
                  {!f.ativo && <Tag style={{ marginLeft: 6 }}>Inativo</Tag>}
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {semCnpj(f)
                      ? <Tag color="warning" style={{ fontSize: 11 }}>sem CNPJ</Tag>
                      : <>CNPJ: <strong style={{ fontFamily: 'monospace' }}>{formatarCnpjCpf(f.cnpj_cpf)}</strong></>}
                    {' · '}<strong>{f.lancamentos}</strong> lançamento(s)
                    {' · '}lançado <strong>{fmtDinheiro(f.total_pago)}</strong>
                    {f.ultimo_lancamento && <> · último {fmtDataBr(f.ultimo_lancamento)}</>}
                  </div>
                </div>
                {fica ? <Tag color="success">✓ Manter</Tag> : <Tag color="error">✗ Apagar</Tag>}
              </Flex>
            </Card>
          );
        })}
      </Space>
    </Modal>
  );
}
